import React, { useRef, useState, useEffect } from "react";
import { Stage, Layer, Rect, Text, Group, Image as KonvaImage, Arc } from "react-konva";

const GRID_SIZE = 28;

// Configuración de tamaño del viewport en cuadrículas
// Por ejemplo: 40 cuadrículas horizontales x 25 verticales = 1120px x 700px
const GRID_COLUMNS_VISIBLE = 40; // Cuántas cuadrículas mostrar horizontalmente
const GRID_ROWS_VISIBLE = 25;    // Cuántas cuadrículas mostrar verticalmente

// Calcular dimensiones del canvas basado en cuadrículas
const CANVAS_WIDTH = GRID_COLUMNS_VISIBLE * GRID_SIZE;  // 40 * 28 = 1120px
const CANVAS_HEIGHT = GRID_ROWS_VISIBLE * GRID_SIZE;    // 25 * 28 = 700px

// Componente para el escenario (readonly - diseño idéntico)
const EscenarioPreview = ({ escenario }) => {
  if (!escenario) return null;

  return (
    <Group x={escenario.x} y={escenario.y}>
      {escenario.tipo === "semicircular" ? (
        <>
          <Arc
            width={escenario.width}
            height={escenario.height}
            angle={180}
            rotation={180}
            fill="#2C3E50"
            stroke="#34495E"
            strokeWidth={3}
          />
          <Text
            text="ESCENARIO"
            fontSize={16}
            fontStyle="bold"
            fill="#FFFFFF"
            align="center"
            verticalAlign="middle"
            width={escenario.width}
            x={0}
            y={escenario.height / 2 - 8}
          />
        </>
      ) : (
        <>
          <Rect
            width={escenario.width}
            height={escenario.height}
            fill="#2C3E50"
            stroke="#34495E"
            strokeWidth={3}
          />
          <Text
            text="ESCENARIO"
            fontSize={16}
            fontStyle="bold"
            fill="#FFFFFF"
            align="center"
            verticalAlign="middle"
            width={escenario.width}
            height={escenario.height}
          />
        </>
      )}
    </Group>
  );
};

// Componente para un asiento (readonly - diseño IDÉNTICO al original)
const AsientoPreview = ({ asiento, sector }) => {
  if (!sector || sector.visible === false) return null;

  let color = sector.color || "#28a745";
  if (asiento.estado === "reservado") color = "#FFA500";
  if (asiento.estado === "vendido") color = "#DC3545";
  if (asiento.estado === "bloqueado") color = "#6C757D";

  // Convertir color hex a rgba (EXACTAMENTE como el original)
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <Group x={asiento.x} y={asiento.y}>
      {/* Rectángulo del asiento - DISEÑO IDÉNTICO */}
      <Rect
        width={asiento.width}
        height={asiento.height}
        fill={hexToRgba(color, 0.3)}
        stroke={color}
        strokeWidth={2}
        cornerRadius={2}
      />
      {/* Número del asiento */}
      <Text
        text={asiento.numero}
        fontSize={10}
        fontStyle="bold"
        fill={color}
        align="center"
        verticalAlign="middle"
        width={asiento.width}
        height={asiento.height}
      />
    </Group>
  );
};

// Componente para elementos especiales (readonly - diseño idéntico)
const ElementoEspecialPreview = ({ elemento }) => {
  let color = "#666666";
  switch (elemento.tipo) {
    case "escalera":
      color = "#95A5A6";
      break;
    case "ingreso":
      color = "#27AE60";
      break;
    case "salida_emergencia":
      color = "#E74C3C";
      break;
    case "baño":
      color = "#3498DB";
      break;
    case "pasillo":
      color = "#BDC3C7";
      break;
    default:
      color = "#7F8C8D";
  }

  const texto = elemento.etiqueta || elemento.tipo.replace('_', ' ');

  return (
    <Group x={elemento.x} y={elemento.y}>
      <Rect
        width={elemento.width}
        height={elemento.height}
        fill={color}
        stroke="#34495E"
        strokeWidth={2}
      />
      <Text
        text={texto.toUpperCase()}
        fontSize={Math.min(elemento.height * 0.4, 16)}
        fontStyle="bold"
        fill="#FFFFFF"
        align="center"
        verticalAlign="middle"
        width={elemento.width}
        height={elemento.height}
      />
    </Group>
  );
};

// Componente para la imagen de fondo (IDÉNTICO al original)
const BackgroundImage = ({ imagenFondo, opacidad, width, height }) => {
  const [image, setImage] = useState(null);

  useEffect(() => {
    if (imagenFondo) {
      const img = new window.Image();
      img.onload = () => {
        if (img.width > 0 && img.height > 0) {
          setImage(img);
        } else {
          console.error("Error: La imagen cargada no tiene dimensiones válidas");
          setImage(null);
        }
      };
      img.onerror = () => {
        console.error("Error al cargar la imagen de fondo");
        setImage(null);
      };
      img.src = imagenFondo;
    } else {
      setImage(null);
    }
  }, [imagenFondo]);

  if (!image || !width || !height || width === 0 || height === 0) {
    return null;
  }

  return (
    <KonvaImage
      image={image}
      width={width}
      height={height}
      opacity={opacidad}
    />
  );
};

const MapaTeatroPreview = ({ mapaData }) => {
  const stageRef = useRef();
  const [zoom, setZoom] = useState(0.5);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [asientos, setAsientos] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [escenario, setEscenario] = useState(null);
  const [elementosEspeciales, setElementosEspeciales] = useState([]);
  const [imagenFondo, setImagenFondo] = useState(null);
  const [opacidadFondo, setOpacidadFondo] = useState(0.3);

  // Dimensiones del canvas basadas en la configuración del mapa
  const [canvasWidth, setCanvasWidth] = useState(CANVAS_WIDTH);
  const [canvasHeight, setCanvasHeight] = useState(CANVAS_HEIGHT);

  useEffect(() => {
    if (mapaData) {
      setAsientos(mapaData.asientos || []);
      setSectores(mapaData.sectores || []);
      setEscenario(mapaData.escenario || {
        x: 400,
        y: 50,
        width: 300,
        height: 60,
        tipo: "semicircular"
      });
      setElementosEspeciales(mapaData.elementosEspeciales || []);
      setImagenFondo(mapaData.imagenFondo || null);
      setOpacidadFondo(mapaData.opacidadFondo || 0.3);

      // Cargar configuración de workspace si existe
      if (mapaData.workspaceGridColumns && mapaData.workspaceGridRows) {
        setCanvasWidth(mapaData.workspaceGridColumns * GRID_SIZE);
        setCanvasHeight(mapaData.workspaceGridRows * GRID_SIZE);
      } else {
        // Valores por defecto
        setCanvasWidth(GRID_COLUMNS_VISIBLE * GRID_SIZE);
        setCanvasHeight(GRID_ROWS_VISIBLE * GRID_SIZE);
      }
    }
  }, [mapaData]);

  const handleWheel = (e) => {
    e.evt.preventDefault();
    const scaleBy = 1.1;
    const stage = stageRef.current;
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    const clampedScale = Math.max(0.1, Math.min(3, newScale));

    setZoom(clampedScale);

    const newPos = {
      x: pointer.x - mousePointTo.x * clampedScale,
      y: pointer.y - mousePointTo.y * clampedScale,
    };

    setStagePos(newPos);
  };

  const handleDragEnd = (e) => {
    setStagePos({
      x: e.target.x(),
      y: e.target.y(),
    });
  };

  return (
    <div className="container-md w-100">
      <div className="border rounded p-3 mb-4" style={{ backgroundColor: "#f8f9fa" }}>

        {/* Header con controles */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 d-flex align-items-center">
            <i className="bi bi-eye me-2 text-primary"></i>
            <strong>Vista Previa del Mapa</strong>
          </h6>
          <div className="d-flex align-items-center gap-2">
            <small className="text-muted me-2">
              {canvasWidth}x{canvasHeight}px
            </small>
            <div className="border-start ps-2 d-flex align-items-center gap-2">
              <small className="text-muted">Zoom: {Math.round(zoom * 100)}%</small>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setZoom(Math.max(0.1, zoom - 0.1))}
                title="Alejar"
              >
                <i className="bi bi-zoom-out"></i>
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setZoom(Math.min(3, zoom + 0.1))}
                title="Acercar"
              >
                <i className="bi bi-zoom-in"></i>
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  setZoom(0.5);
                  setStagePos({ x: 0, y: 0 });
                }}
                title="Restablecer vista"
              >
                <i className="bi bi-arrow-clockwise"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Alert informativo */}
        <div className="alert alert-info d-flex align-items-start mb-3">
          <i className="bi bi-info-circle me-2 mt-1"></i>
          <div>
            <small>
              <strong>Vista de solo lectura.</strong> Este mapa corresponde a la configuración de asientos de la sala.
              Los asientos se gestionan desde el creador de mapas de la sala.
              <br />
              <strong>Tip:</strong> Usa la rueda del mouse o los botones para hacer zoom. Arrastra el canvas para moverte.
              {imagenFondo && (
                <>
                  <br />
                  <strong>Nota:</strong> La imagen de fondo se ajusta al tamaño del viewport ({canvasWidth}x{canvasHeight}px).
                </>
              )}
            </small>
          </div>
        </div>

        {/* Canvas del mapa - DISEÑO IDÉNTICO */}
        <div
          className="border rounded"
          style={{
            backgroundColor: "#ffffff",
            overflow: "hidden",
            boxShadow: "inset 0 0 10px rgba(0,0,0,0.1)"
          }}
        >
          <Stage
            ref={stageRef}
            width={canvasWidth}
            height={canvasHeight}
            scaleX={zoom}
            scaleY={zoom}
            x={stagePos.x}
            y={stagePos.y}
            onWheel={handleWheel}
            draggable={true}
            onDragEnd={handleDragEnd}
          >
            <Layer>
              {/* Grid de fondo - IDÉNTICO al original */}
              {Array.from({ length: Math.ceil(5000 / GRID_SIZE) }).map((_, i) => (
                <React.Fragment key={`grid-${i}`}>
                  <Rect
                    x={i * GRID_SIZE}
                    y={0}
                    width={1}
                    height={5000}
                    fill="#f0f0f0"
                  />
                  <Rect
                    x={0}
                    y={i * GRID_SIZE}
                    width={5000}
                    height={1}
                    fill="#f0f0f0"
                  />
                </React.Fragment>
              ))}

              {/* Imagen de fondo - Usa las mismas dimensiones que el canvas */}
              {imagenFondo && (
                <BackgroundImage
                  imagenFondo={imagenFondo}
                  opacidad={opacidadFondo}
                  width={canvasWidth}
                  height={canvasHeight}
                />
              )}

              {/* Escenario */}
              {escenario && <EscenarioPreview escenario={escenario} />}

              {/* Elementos especiales */}
              {elementosEspeciales.map((elemento) => (
                <ElementoEspecialPreview
                  key={elemento.id}
                  elemento={elemento}
                />
              ))}

              {/* Asientos - RENDERIZADO IDÉNTICO */}
              {asientos.map((asiento) => {
                const sector = sectores.find((s) => s.id == asiento.sector_id);
                return (
                  <AsientoPreview
                    key={asiento.id}
                    asiento={asiento}
                    sector={sector}
                  />
                );
              })}
            </Layer>
          </Stage>
        </div>

        {/* Leyenda de sectores */}
        {sectores.length > 0 && (
          <div className="mt-3 p-3 bg-white border rounded">
            <h6 className="mb-3 d-flex align-items-center">
              <i className="bi bi-palette me-2 text-success"></i>
              <strong>Sectores del Mapa</strong>
            </h6>
            <div className="row">
              {sectores.map((sector) => (
                <div key={sector.id} className="col-md-6 col-lg-4 mb-2">
                  <div className="d-flex align-items-center p-2 bg-light rounded">
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        backgroundColor: sector.color || "#28a745",
                        borderRadius: "3px",
                        marginRight: "10px",
                        border: "2px solid #dee2e6",
                        flexShrink: 0
                      }}
                    ></div>
                    <div className="flex-grow-1">
                      <strong className="d-block">{sector.nombre}</strong>
                      <small className="text-muted">
                        {sector.stock || sector.capacidad || 0} asientos
                        {sector.precio > 0 && (
                          <> • ${sector.precio.toLocaleString()}</>
                        )}
                      </small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Estadísticas */}
        <div className="mt-3 p-3 bg-light border rounded">
          <div className="row text-center">
            <div className="col-4">
              <div className="d-flex flex-column align-items-center">
                <i className="bi bi-circle-fill text-primary mb-2" style={{ fontSize: "24px" }}></i>
                <div className="h4 mb-0 text-primary fw-bold">{asientos.length}</div>
                <small className="text-muted">Asientos Totales</small>
              </div>
            </div>
            <div className="col-4">
              <div className="d-flex flex-column align-items-center">
                <i className="bi bi-grid-3x3 text-success mb-2" style={{ fontSize: "24px" }}></i>
                <div className="h4 mb-0 text-success fw-bold">{sectores.length}</div>
                <small className="text-muted">Sectores</small>
              </div>
            </div>
            <div className="col-4">
              <div className="d-flex flex-column align-items-center">
                <i className="bi bi-box text-info mb-2" style={{ fontSize: "24px" }}></i>
                <div className="h4 mb-0 text-info fw-bold">{elementosEspeciales.length}</div>
                <small className="text-muted">Elementos Extra</small>
              </div>
            </div>
          </div>
        </div>

        {/* Leyenda de estados de asientos */}
        <div className="mt-3 p-3 bg-white border rounded">
          <h6 className="mb-3 d-flex align-items-center">
            <i className="bi bi-info-circle me-2 text-secondary"></i>
            <strong>Leyenda de Estados</strong>
          </h6>
          <div className="row">
            <div className="col-6 col-md-3 mb-2">
              <div className="d-flex align-items-center">
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    backgroundColor: "rgba(40, 167, 69, 0.3)",
                    border: "2px solid #28a745",
                    borderRadius: "2px",
                    marginRight: "8px"
                  }}
                ></div>
                <small><strong>Disponible</strong></small>
              </div>
            </div>
            <div className="col-6 col-md-3 mb-2">
              <div className="d-flex align-items-center">
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    backgroundColor: "rgba(255, 165, 0, 0.3)",
                    border: "2px solid #FFA500",
                    borderRadius: "2px",
                    marginRight: "8px"
                  }}
                ></div>
                <small><strong>Reservado</strong></small>
              </div>
            </div>
            <div className="col-6 col-md-3 mb-2">
              <div className="d-flex align-items-center">
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    backgroundColor: "rgba(220, 53, 69, 0.3)",
                    border: "2px solid #DC3545",
                    borderRadius: "2px",
                    marginRight: "8px"
                  }}
                ></div>
                <small><strong>Vendido</strong></small>
              </div>
            </div>
            <div className="col-6 col-md-3 mb-2">
              <div className="d-flex align-items-center">
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    backgroundColor: "rgba(108, 117, 125, 0.3)",
                    border: "2px solid #6C757D",
                    borderRadius: "2px",
                    marginRight: "8px"
                  }}
                ></div>
                <small><strong>Bloqueado</strong></small>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default MapaTeatroPreview;
